import type { Answer, Axis, CellKind, ClueDirection, Coord, Puzzle } from './types.ts';

/** The parts of a puzzle the grid helpers read, so callers can pass partial shapes. */
export type GridLike = Pick<Puzzle, 'width' | 'height' | 'grid'>;

const LETTER = /^[A-Z]$/;

export function inBounds(puzzle: Pick<Puzzle, 'width' | 'height'>, coord: Coord): boolean {
	const { row, col } = coord;
	return (
		Number.isInteger(row) &&
		Number.isInteger(col) &&
		row >= 0 &&
		col >= 0 &&
		row < puzzle.height &&
		col < puzzle.width
	);
}

/** What a grid cell is, or `undefined` if the coordinate is outside the grid. */
export function cellKind(puzzle: GridLike, coord: Coord): CellKind | undefined {
	if (!inBounds(puzzle, coord)) return undefined;
	const char = puzzle.grid[coord.row]?.[coord.col];
	if (char === '#') return 'clue';
	if (char === '.') return 'blank';
	if (char !== undefined && LETTER.test(char)) return 'letter';
	return undefined;
}

/** The solution letter at a letter cell, or `undefined` for any other cell. */
export function solutionLetter(puzzle: GridLike, coord: Coord): string | undefined {
	return cellKind(puzzle, coord) === 'letter' ? puzzle.grid[coord.row]?.[coord.col] : undefined;
}

/** The coordinate `n` cells along `axis` from `coord` (negative `n` steps back). */
export function step(coord: Coord, axis: Axis, n = 1): Coord {
	return axis === 'across'
		? { row: coord.row, col: coord.col + n }
		: { row: coord.row + n, col: coord.col };
}

/** The cells an answer occupies, in reading order. They may lie outside the grid. */
export function answerCells(answer: Pick<Answer, 'start' | 'axis' | 'text'>): Coord[] {
	return Array.from({ length: answer.text.length }, (_, i) => step(answer.start, answer.axis, i));
}

/** Where a clue's arrow sends the answer: the offset of its first cell, and its axis. */
const ARROWS: Record<ClueDirection, { dRow: number; dCol: number; axis: Axis }> = {
	right: { dRow: 0, dCol: 1, axis: 'across' },
	down: { dRow: 1, dCol: 0, axis: 'down' },
	'right-down': { dRow: 0, dCol: 1, axis: 'down' },
	'down-right': { dRow: 1, dCol: 0, axis: 'across' },
};

/** The start cell and axis of the answer a clue at `cell` with this arrow points to. */
export function clueAnchor(direction: ClueDirection, cell: Coord): { start: Coord; axis: Axis } {
	const { dRow, dCol, axis } = ARROWS[direction];
	return { start: { row: cell.row + dRow, col: cell.col + dCol }, axis };
}

export function sameCoord(a: Coord, b: Coord): boolean {
	return a.row === b.row && a.col === b.col;
}

/** A stable string key for maps and sets keyed by cell. */
export function coordKey(coord: Coord): string {
	return `${coord.row},${coord.col}`;
}

/** Human-readable, zero-based, matching the JSON: `(row 1, col 3)`. */
export function formatCoord(coord: Coord): string {
	return `(row ${coord.row}, col ${coord.col})`;
}
